import { modalSignal } from "./ModalContainer"
import infoIcon from "../../../assets/icons/icon-information.svg"
import binIcon from "../../../assets/icons/icon-bin-red.svg"
import LoadingButton from "../LoadingButton"
import { useRef, useState } from "react"
import { ISpecimen } from "../entityComponents/Form/components/FormSpecimens"

interface ISpecimensModal {
    heading: string,
    information: string,
    ContinueFunc: Function,
    cancelFunc?: Function
    autoClose?: boolean | null | undefined,
    existingSpecimens: ISpecimen[],
}

const quantityValidationChecks = {
    isPositiveInteger: (value: string | null | undefined) => ({
        isValid: /^[1-9]\d*$/.test(value ?? ""),
        errorMessage: "Quantity must be a positive, whole number",
    }),
    hasNoSpecialChars: (value: string | null | undefined) => {
        const invalidChars = ["-", "+", "e", "E", " "];
        let containsInvalid = false;

        if (value) {
            for (let i = 0; i < value.length; i++) {
                if (invalidChars.includes(value[i])) {
                    containsInvalid = true;
                    break;
                }
            }
        }
        return {
            isValid: containsInvalid === false,
            errorMessage: `Quantity cannot have special characters: ${invalidChars.join(", ")}`,
        };
    },
};

const codeValidationChecks = {
    isDefined: (value: string | null | undefined) => ({
        isValid: !!value,
        errorMessage: "Specimen number cannot be empty",
    }),
};

export default function SpecimensModal({ heading, information, ContinueFunc, cancelFunc, autoClose, existingSpecimens }: ISpecimensModal) {
    const [loading, setLoading] = useState<boolean>(false);
    const [specimens, setSpecimens] = useState<ISpecimen[]>([...existingSpecimens]);
    const [error, setError] = useState<string | undefined>(undefined);

    const specimenCodeRef = useRef<HTMLInputElement | null>(null);
    const specimenQuantityRef = useRef<HTMLInputElement | null>(null);
    const specimenListRef = useRef<HTMLInputElement | null>(null);

    async function handleContinue() {
        const finalSpecimens = addSpecimenIfPresent();
        modalSignal.value = undefined;
        ContinueFunc(finalSpecimens);
    }

    function handleCodeKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Tab") {
            event.preventDefault();
            setTimeout(() => {
                if (specimenQuantityRef.current) {
                    specimenQuantityRef.current.focus();
                }
            }, 0); // Wait for the DOM to update
        }
    }

    function handleQuantityKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Tab") {
            event.preventDefault();
            scrollSpecimenListToBottom();
            setTimeout(() => {
                if (specimenCodeRef.current) {
                    specimenCodeRef.current.focus();
                }
            }, 0); // Wait for the DOM to update
            addSpecimenIfPresent();
            return;
        }
    }

    function handleCodeKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key !== "Tab") {
            for (const check of Object.values(codeValidationChecks)) {
                const { isValid, errorMessage } = check(String(event.currentTarget.value));
                if (!isValid) {
                    setError(errorMessage);
                    return;
                };
            }
            setError(undefined);
        }
    }

    function handleQuantityKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key !== "Tab") {
            for (const check of Object.values(quantityValidationChecks)) {
                const { isValid, errorMessage } = check(String(event.currentTarget.value));
                if (!isValid) {
                    setError(errorMessage);
                    return;
                };
            }
            setError(undefined);
        }
    }

    function handleQuantityWheel(event: React.WheelEvent<HTMLInputElement>) {
        // Prevent the default behavior of scroll wheel changing the input value
        event.preventDefault();
    }

    function scrollSpecimenListToBottom() {

        if (specimenListRef.current) {
            setTimeout(() => {
                const container = specimenListRef.current;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 0); // Wait for the DOM to update
        }
    }

    function addSpecimenIfPresent() {
        const code = specimenCodeRef.current?.value;
        const quantity = Number(specimenQuantityRef.current?.value);

        if (code === null || code === undefined || code.trim() === "" || isNaN(quantity) || quantity < 1) {
            return specimens;
        }

        const newSpecimen = {
            id: null,
            code: code,
            quantity: quantity,
        };

        const updatedSpecimens = [...specimens, newSpecimen];
        setSpecimens(updatedSpecimens);

        specimenCodeRef.current!.value = "";
        specimenQuantityRef.current!.value = "";

        return updatedSpecimens;
    }

    function updateSpecimen(index: number, field: keyof ISpecimen, value: string | number) {
        setSpecimens((prevSpecimens: ISpecimen[]) =>
            prevSpecimens.map((specimen, i) =>
                i === index ? { ...specimen, [field]: value } : specimen
            )
        );
    }

    function removeSpecimen(index: number) {
        setSpecimens((prevSpecimens: ISpecimen[]) =>
            prevSpecimens.filter((_, i) => i !== index)
        );
    }

    function getSpecimenItem(specimen: ISpecimen, index: number) {
        return (
            <div key={index} className="form-specimens__specimen-row">
                <input
                    className="input-text"
                    type="text"
                    value={specimen.code || ""}
                    onWheel={handleQuantityWheel}
                    onChange={(e) => updateSpecimen(index, "code", e.target.value)}
                />
                <input
                    className="input-text"
                    type="number"
                    value={specimen.quantity || ""}
                    onWheel={handleQuantityWheel}
                    onChange={(e) => updateSpecimen(index, "quantity", Number(e.target.value))}
                />
                <div className="form-specimens__delete-specimen-wrapper" onClick={() => removeSpecimen(index)}>
                    <img src={binIcon} />
                </div>
            </div>
        );
    }

    function hasError() {
        return error !== "" && error !== null && error !== undefined;
    }

    return (
        <div className="display--flex flex__direction--column alignItems--center gap--xs">
            <img src={infoIcon} />
            <h1>{heading}</h1>
            <p className="textAlign--center whitespace--pre-line">{information}</p>
            <div>
                <div ref={specimenListRef} className="form-specimens__specimen-list">
                    {specimens.map((s: ISpecimen, index: number) => getSpecimenItem(s, index))}
                </div>
                <hr />
                <div className="form-specimens__add-new">
                    <input
                        placeholder="Enter specimen number"
                        className="input-text"
                        type="text"
                        ref={specimenCodeRef}
                        onKeyUp={handleCodeKeyUp}
                        onKeyDown={handleCodeKeyDown}
                    />
                    <input
                        placeholder="Enter quantity"
                        className="input-text"
                        type="number"
                        ref={specimenQuantityRef}
                        onWheel={handleQuantityWheel}
                        onKeyUp={handleQuantityKeyUp}
                        onKeyDown={handleQuantityKeyDown}
                    />
                </div>
                <div>
                    {error && <p className="form-specimens__error small">{error}</p>}
                </div>
            </div>
            <div className="margin__top--xs display--flex gap--xs">
                <LoadingButton
                    className="button button--large button--primary button--with-icon"
                    onPress={handleContinue}
                    buttonText="Done"
                    loadingText=""
                    isDisabled={loading || hasError()}
                    isLoading={loading}>
                    <></>
                </LoadingButton>
            </div>
        </div>
    );
}



